import { useApolloClient } from '@apollo/client'

import { useCallback, useRef } from 'react'
import { useShallow } from 'zustand/react/shallow'

import type { MenuItem } from '../domain/menu-item'
import { createMenuItems } from '../domain/menu-item'
import type {
  VendorCategoryItemsQueryData,
  VendorCategoryItemsQueryVariables,
} from '../states/vendorMenuQueries'
import { GET_VENDOR_CATEGORY_ITEMS } from '../states/vendorMenuQueries'
import {
  useCategoryItemsStore,
  useCategoryLoadState,
  type CategoryLoadState,
} from '../store/categoryItemsStore'

type UseLazyCategoryItemsOptions = {
  vendorId: string
  categoryId: string
  filters?: VendorCategoryItemsQueryVariables['filters']
  limit?: number
}

type UseLazyCategoryItemsResult = {
  loadState: CategoryLoadState
  loading: boolean
  loadFullItems: () => Promise<MenuItem[] | null>
  fullItems: MenuItem[] | undefined
  error: string | null
}

export const useLazyCategoryItems = ({
  vendorId,
  categoryId,
  filters,
  limit = 100,
}: UseLazyCategoryItemsOptions): UseLazyCategoryItemsResult => {
  const apolloClient = useApolloClient()
  const loadState = useCategoryLoadState(categoryId)
  const fullItems = useCategoryItemsStore((state) => state.fullItemsByCategory[categoryId])
  const error = useCategoryItemsStore((state) => state.errorsByCategory[categoryId] ?? null)

  const { setFullItems, setCategoryLoadState, setCategoryError } = useCategoryItemsStore(
    useShallow((state) => ({
      setFullItems: state.setFullItems,
      setCategoryLoadState: state.setCategoryLoadState,
      setCategoryError: state.setCategoryError,
    })),
  )

  const loadingRef = useRef(false)

  const loadFullItems = useCallback(async (): Promise<MenuItem[] | null> => {
    if (loadState === 'loaded' && fullItems) {
      return fullItems
    }

    if (loadingRef.current || loadState === 'loading') {
      return null
    }

    loadingRef.current = true
    setCategoryLoadState(categoryId, 'loading')

    try {
      const allItems: MenuItem[] = []
      let offset = 0
      let hasMore = true

      while (hasMore) {
        const { data, error: queryError } = await apolloClient.query<
          VendorCategoryItemsQueryData,
          VendorCategoryItemsQueryVariables
        >({
          query: GET_VENDOR_CATEGORY_ITEMS,
          variables: {
            vendorId,
            categoryId,
            filters: filters ?? null,
            offset,
            limit,
          },
          fetchPolicy: 'network-only',
        })

        if (queryError) {
          throw queryError
        }

        const items = data.vendorCategoryItems.items
        if (items && items.length > 0) {
          const menuItems = createMenuItems(items)
          allItems.push(...menuItems)
        }

        hasMore = data.vendorCategoryItems.pageInfo.hasMore
        offset = data.vendorCategoryItems.pageInfo.nextOffset ?? offset + limit
      }

      setFullItems(categoryId, allItems)
      loadingRef.current = false
      return allItems
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load category items'
      setCategoryError(categoryId, errorMessage)
      loadingRef.current = false
      return null
    }
  }, [
    apolloClient,
    vendorId,
    categoryId,
    filters,
    limit,
    loadState,
    fullItems,
    setFullItems,
    setCategoryLoadState,
    setCategoryError,
  ])

  return {
    loadState,
    loading: loadState === 'loading',
    loadFullItems,
    fullItems,
    error,
  }
}
